/**
 * Admin Onboarding — Email Verification Required
 * ================================================
 *
 * TREATX-ADMIN-ONBOARDING-01 · frontend
 *
 * Waiting-room page shown after the password has been changed but
 * before the verification email link has been consumed. Provides
 * a "Send verification email" / resend action and confirms delivery
 * status. Backend flags remain authoritative — the "onboarded"
 * status refreshed on mount is the source of truth.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2, CheckCircle2, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function AdminOnboardingVerifyRequired() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await api.get('/admin/onboarding/status');
      setStatus(data);
      if (data?.must_change_password) {
        navigate('/admin/onboarding/change-password', { replace: true });
        return;
      }
      if (data?.email_verified) {
        // Both flags cleared — go to the admin workspace.
        navigate('/treatx-admin/dashboard', { replace: true });
        return;
      }
    } catch (e) {
      setErr('Could not check onboarding state.');
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const sendEmail = async () => {
    setSending(true); setErr('');
    try {
      const { data } = await api.post('/admin/onboarding/start-email-verification', {});
      if (data?.success) {
        setSent(true);
        toast.success('Verification email sent', {
          description: `We've sent a verification link to ${data.delivered_to}.`,
        });
      }
    } catch (ex) {
      const detail = ex?.response?.data?.detail
        || ex?.response?.data?.message
        || ex?.message
        || 'Failed to send verification email.';
      setErr(String(detail));
    }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking onboarding state…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
         data-testid="admin-onboarding-verify-required">
      <Card className="w-full max-w-md shadow-lg border border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <Mail className="h-6 w-6 text-emerald-600" />
          </div>
          <CardTitle className="text-slate-800 font-[Manrope] text-xl">
            Verify your admin email
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            One more step before you can use privileged Platform Admin actions.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="text-[10px] uppercase text-slate-500">Address</div>
            <div className="font-mono text-slate-800" data-testid="onboarding-email-address">
              {status?.email || '—'}
            </div>
          </div>
          {!sent ? (
            <Button
              onClick={sendEmail}
              disabled={sending}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="onboarding-send-email"
            >
              {sending ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>)
                       : (<><Mail className="h-4 w-4 mr-2" /> Send verification email</>)}
            </Button>
          ) : (
            <>
              <div className="flex items-start gap-2 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 text-sm"
                   data-testid="onboarding-email-sent">
                <CheckCircle2 className="h-4 w-4 mt-0.5" />
                <div>
                  Email sent. Open the message and click <b>Verify my email</b>.
                  The link expires in 24 hours and can be used only once.
                </div>
              </div>
              <Button
                onClick={sendEmail}
                disabled={sending}
                variant="outline"
                className="w-full"
                data-testid="onboarding-resend-email"
              >
                {sending ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resending…</>)
                         : (<><RefreshCw className="h-4 w-4 mr-2" /> Resend email</>)}
              </Button>
            </>
          )}
          {err && (
            <div className="text-xs text-rose-700 border border-rose-200 bg-rose-50 rounded p-2"
                 data-testid="onboarding-error">
              {err}
            </div>
          )}
          <Button
            variant="ghost"
            onClick={refresh}
            className="w-full text-slate-500 hover:text-slate-800 text-xs"
            data-testid="onboarding-refresh-status"
          >
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh status
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
